import dayjs from 'dayjs';
import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import { SearchResultResponse } from '../../api/CoreSearchApi';
import {
	CARD_AMOUNT,
	CARD_NAME,
	NOT_AVAILABLE_LABEL,
	SEARCH_CARD_POLICY_TAG,
} from '../../data/labelsAndMessages';
import { CurrencyCode } from '../../types/currencyCode';
import { capitalize, formatCurrency, formatName } from '../../util/util';
import { CORE_SEARCH_RESULTS_TYPES } from './constants';

export const getPolicyholderData = (searchResult: SearchResultResponse) => {
	const title1 = `Policyholder ${
		searchResult.policyholderLocator || NOT_AVAILABLE_LABEL()
	}`;
	const value = `/policyholder/${searchResult.policyholderLocator || ''}`;
	const type = 'policyholder';
	let subtitle1 = null;

	if (!!searchResult.searchSummary?.length) {
		subtitle1 = searchResult.searchSummary
			.slice(0, 3)
			.filter((item) => !!item.fieldValue?.length)
			.map((item) => item.fieldValue[0])
			.join(' ');
	}

	return { value, title1, subtitle1, type };
};

const getSearchSummaryByFieldName = (
	searchResult: SearchResultResponse,
	fieldName: string,
) => {
	return searchResult.searchSummary.find(
		(item) => item?.fieldName === fieldName,
	)?.fieldValue?.[0];
};

const getPolicySummary = (searchResult: SearchResultResponse) => {
	let productName = getSearchSummaryByFieldName(searchResult, 'productName');
	productName = productName ? `${formatName(productName)} ` : '';
	const startTimestamp = getSearchSummaryByFieldName(
		searchResult,
		'policyStartTimestamp',
	);
	const endTimestamp = getSearchSummaryByFieldName(
		searchResult,
		'policyEndTimestamp',
	);

	return { productName, startTimestamp, endTimestamp };
};

export const getPolicyOption = (searchResult: SearchResultResponse) => {
	const { productName, startTimestamp, endTimestamp } =
		getPolicySummary(searchResult);
	const title1 = `${productName}Policy ${
		searchResult.policyLocator || NOT_AVAILABLE_LABEL()
	}`;
	const subtitle1 =
		!!startTimestamp && startTimestamp !== '0'
			? dayjs(parseInt(startTimestamp)).format('YYYY/MM/DD')
			: null;
	const subtitle2 = !!endTimestamp
		? dayjs(parseInt(endTimestamp)).format('YYYY/MM/DD')
		: null;
	const value = `/policy/${searchResult.policyLocator || ''}`;
	const type = 'policy';

	return { value, title1, subtitle1, subtitle2, type };
};

export const getQuoteOption = (searchResult: SearchResultResponse) => {
	const { productName } = getPolicySummary(searchResult);
	const state = getSearchSummaryByFieldName(searchResult, 'state');
	const name = getSearchSummaryByFieldName(searchResult, 'name');
	const policyLocator = searchResult.policyLocator;
	const policyTag = policyLocator ? SEARCH_CARD_POLICY_TAG(policyLocator) : '';
	const title1 = `${productName}Quote ${
		searchResult.quoteLocator || NOT_AVAILABLE_LABEL()
	}${policyTag}`;
	const title2 = !!state ? `${capitalize(state)}` : null;
	const subtitle1 = !!name ? `${CARD_NAME()} ${name}` : null;
	let value = '';

	if (policyLocator && searchResult.quoteLocator) {
		value = `/policy/${policyLocator}/quotes/${searchResult.quoteLocator}`;
	} else if (policyLocator) {
		value = `/policy/${policyLocator}`;
	}

	const type = 'quote';

	return { value, title1, title2, subtitle1, type };
};

export const getPremiumReportOption = (searchResult: SearchResultResponse) => {
	const startTimestamp = getSearchSummaryByFieldName(
		searchResult,
		'startTimestamp',
	);
	const endTimestamp = getSearchSummaryByFieldName(
		searchResult,
		'endTimestamp',
	);
	const reportName = getSearchSummaryByFieldName(searchResult, 'reportName');
	const title1 = `Premium Report ${
		searchResult.premiumReportLocator || NOT_AVAILABLE_LABEL()
	}`;
	const title2 = !!reportName ? reportName : null;
	const subtitle1 =
		!!startTimestamp && startTimestamp !== '0'
			? dayjs(parseInt(startTimestamp)).format('YYYY/MM/DD')
			: null;
	const subtitle2 = !!endTimestamp
		? dayjs(parseInt(endTimestamp)).format('YYYY/MM/DD')
		: null;
	const value =
		!!searchResult.policyLocator && !!searchResult.premiumReportLocator
			? `/policy/${searchResult.policyLocator}/premiumReports/${searchResult.premiumReportLocator}`
			: null;

	const type = 'premiumReport';

	return { value, title1, subtitle1, title2, subtitle2, type };
};

export const getClaimOption = (searchResult: SearchResultResponse) => {
	let productName = getSearchSummaryByFieldName(searchResult, 'productName');
	const currentStatus = getSearchSummaryByFieldName(
		searchResult,
		'currentStatus',
	);
	const notificationTimestamp = getSearchSummaryByFieldName(
		searchResult,
		'notificationTimestamp',
	);
	const incidentTimestamp = getSearchSummaryByFieldName(
		searchResult,
		'incidentTimestamp',
	);
	productName = !!productName ? `${formatName(productName)} ` : '';
	const isSubclaim = !!searchResult.subclaimLocator;
	const locator = isSubclaim
		? searchResult.subclaimLocator
		: searchResult.claimLocator;
	const title1 = `${productName}${!!isSubclaim ? 'Subclaim' : 'Claim'} ${
		locator || NOT_AVAILABLE_LABEL()
	}`;
	const title2 = !!currentStatus ? `${capitalize(currentStatus)}` : null;
	const subtitle1 = !!notificationTimestamp
		? `${dayjs(parseInt(notificationTimestamp)).format('YYYY/MM/DD')}`
		: null;
	const subtitle2 = !!incidentTimestamp
		? `${dayjs(parseInt(incidentTimestamp)).format('YYYY/MM/DD')}`
		: null;

	let value = '';

	if (searchResult.policyLocator && searchResult.claimLocator) {
		value = `/policy/${searchResult.policyLocator}/claims/${searchResult.claimLocator}`;
	} else if (searchResult.policyLocator) {
		value = `/policy/${searchResult.policyLocator}`;
	}
	const type = 'claim';

	return { value, title1, subtitle1, title2, subtitle2, type };
};

export const getPaymentOption = (
	searchResult: SearchResultResponse,
	cashManagementEnabled: boolean,
) => {
	const title1 = `Payment ${
		searchResult.paymentDisplayId || NOT_AVAILABLE_LABEL()
	}`;
	const amount = getSearchSummaryByFieldName(searchResult, 'amount');
	const invoiceDisplayId = getSearchSummaryByFieldName(
		searchResult,
		'invoiceDisplayId',
	);
	const currency = getSearchSummaryByFieldName(
		searchResult,
		'currency',
	) as CurrencyCode;
	const subtitle1 = !!amount
		? `${CARD_AMOUNT()} ${formatCurrency(parseFloat(amount), currency)}`
		: null;
	const subtitle2 = !!invoiceDisplayId ? `Invoice ${invoiceDisplayId}` : null;
	let value = '';
	if (cashManagementEnabled) {
		value = `/payments/${searchResult.paymentLocator}`;
	} else if (
		searchResult.policyLocator &&
		searchResult.premiumReportLocator &&
		invoiceDisplayId
	) {
		value = `/policy/${searchResult.policyLocator}/premiumReports/${searchResult.premiumReportLocator}/invoices/${invoiceDisplayId}`;
	} else if (searchResult.policyLocator && invoiceDisplayId) {
		value = `/policy/${searchResult.policyLocator}/invoices/${invoiceDisplayId}`;
	} else if (searchResult.policyLocator) {
		value = `/policy/${searchResult.policyLocator}`;
	}
	const type = 'payment';

	return { value, title1, subtitle1, subtitle2, type };
};

interface SearchResultsData {
	title1: any;
	title2?: any;
	value?: string;
	label?: string;
	subtitle1?: any;
	type?: string;
}

export const getSearchResultData = (
	searchResult: SearchResultResponse,
	cashManagementEnabled: boolean,
): SearchResultsData => {
	switch (searchResult.type) {
		case CORE_SEARCH_RESULTS_TYPES.POLICYHOLDER:
			return getPolicyholderData(searchResult);
		case CORE_SEARCH_RESULTS_TYPES.POLICY:
			return getPolicyOption(searchResult);
		case CORE_SEARCH_RESULTS_TYPES.QUOTE:
			return getQuoteOption(searchResult);
		case CORE_SEARCH_RESULTS_TYPES.PREMIUM_REPORT:
			return getPremiumReportOption(searchResult);
		case CORE_SEARCH_RESULTS_TYPES.CLAIM:
			return getClaimOption(searchResult);
		case CORE_SEARCH_RESULTS_TYPES.PAYMENT:
			return getPaymentOption(searchResult, cashManagementEnabled);
		default:
			return null;
	}
};

export const useArrowUpOrDown = (
	arrowUpCallBack: (e?: any) => void,
	arrowDownCallBack: (e?: any) => void,
	node = null as any,
) => {
	// implement the callback ref pattern
	const arrowUpCallBackRef = useRef(arrowUpCallBack);
	const arrowDownCallBackRef = useRef(arrowDownCallBack);
	useLayoutEffect(() => {
		arrowUpCallBackRef.current = arrowUpCallBack;
		arrowDownCallBackRef.current = arrowDownCallBack;
	});

	// handle what happens on key press
	const handleKeyPress = useCallback((event) => {
		// Arrow down
		if (event.keyCode === 40) {
			arrowDownCallBackRef.current(event);
		}
		// Arrow up
		if (event.keyCode === 38) {
			arrowUpCallBackRef.current(event);
		}
	}, []);

	useEffect(() => {
		// target is either the provided node or the document
		const targetNode = node ?? document;
		// attach the event listener
		targetNode && targetNode.addEventListener('keydown', handleKeyPress);

		// remove the event listener
		return () =>
			targetNode && targetNode.removeEventListener('keydown', handleKeyPress);
	}, [handleKeyPress, node]);
};

// see https://github.com/tannerlinsley/react-query/issues/293
// see https://usehooks.com/useDebounce/
export default function useDebounce(value: string, delay: number) {
	// State and setters for debounced value
	const [debouncedValue, setDebouncedValue] = useState(value);

	useEffect(
		() => {
			// Update debounced value after delay
			const handler = setTimeout(() => {
				setDebouncedValue(value);
			}, delay);

			// Cancel the timeout if value changes (also on delay change or unmount)
			// This is how we prevent debounced value from updating if value is changed ...
			// .. within the delay period. Timeout gets cleared and restarted.
			return () => {
				clearTimeout(handler);
			};
		},
		[value, delay], // Only re-call effect if value or delay changes
	);

	return debouncedValue;
}
